import {
  getSpaPackageConfigGroups,
  getSpaPackageConfigGroupsKey,
  mergeConfigGroupTimeslots,
} from '@features/spa/hooks/useGetSpaPackageConfigGroups';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';

const CACHE_TIME = 60000;

export const configGroupsCacheKey = (date: string, quantity: number) => `${date}|${quantity}`;

interface UseExperienceConfigGroupsOptions {
  /** One entry per distinct date and party size the page needs timeslots for. */
  requests: { date: string; quantity: number }[];
  configurationGroups?: ConfigurationGroupType[];
  propertyId?: number;
  spaPackageId?: number;
  spaOpenTime?: string;
  spaCloseTime?: string;
}

export interface ExperienceConfigGroups {
  byKey: Record<string, ConfigurationGroupType[]>;
  loadingKeys: Record<string, boolean>;
  errorKeys: Record<string, boolean>;
  hasError: boolean;
}

/**
 * Fetches spa configuration groups for every date and party size the step
 * shows at once, rather than one request per rendered card. Group mode asks
 * for the whole party, separate mode asks for one guest at a time.
 */
export const useExperienceConfigGroups = ({
  requests,
  configurationGroups,
  propertyId,
  spaPackageId,
  spaOpenTime,
  spaCloseTime,
}: UseExperienceConfigGroupsOptions): ExperienceConfigGroups => {
  const requestsKey = requests
    .map(({ date, quantity }) => configGroupsCacheKey(date, quantity))
    .sort()
    .join(',');

  const uniqueRequests = useMemo(
    () =>
      requestsKey
        ? Array.from(new Set(requestsKey.split(','))).map((key) => {
            const [date, quantity] = key.split('|');
            return { date, quantity: Number(quantity) };
          })
        : [],
    [requestsKey]
  );

  const isEnabled = !!propertyId && !!spaPackageId && !!spaOpenTime;

  const results = useQueries({
    queries: uniqueRequests.map(({ date, quantity }) => ({
      queryKey: getSpaPackageConfigGroupsKey(
        Number(propertyId),
        Number(spaPackageId),
        date,
        quantity,
        spaOpenTime,
        spaCloseTime
      ),
      queryFn: () =>
        getSpaPackageConfigGroups(
          Number(propertyId),
          Number(spaPackageId),
          date,
          quantity,
          spaOpenTime,
          spaCloseTime
        ),
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
      enabled: isEnabled,
    })),
  });

  // Includes status and fetching, not only dataUpdatedAt, or a request that
  // starts, fails or finishes without new data leaves the derived state stale.
  const resultsSignature = results
    .map((result) => `${result.status}:${result.isFetching ? 1 : 0}:${result.dataUpdatedAt}`)
    .join(',');

  return useMemo(() => {
    const byKey: Record<string, ConfigurationGroupType[]> = {};
    const loadingKeys: Record<string, boolean> = {};
    const errorKeys: Record<string, boolean> = {};

    uniqueRequests.forEach(({ date, quantity }, index) => {
      const key = configGroupsCacheKey(date, quantity);
      const result = results[index];
      loadingKeys[key] = isEnabled && Boolean(result?.isLoading || result?.isFetching);
      errorKeys[key] = Boolean(result?.isError);
      byKey[key] = result?.data ? mergeConfigGroupTimeslots(configurationGroups, result.data) : [];
    });

    return {
      byKey,
      loadingKeys,
      errorKeys,
      hasError: Object.values(errorKeys).some(Boolean),
    };
  }, [uniqueRequests, configurationGroups, resultsSignature, isEnabled]);
};
